import type { VoyageReportKind } from './schemas.js';

/**
 * All Reports (ADR 0082; ortağın `sefer-2.jpeg`, `sefer-6.jpeg`): geminin bütün sefer raporları tek
 * kronolojik listede. Menüdeki dokuz rapor türü operasyon akışı sırasıyla; her biri bir ya da
 * birkaç kayıt türünü toplar (Arrival = EOSP + Berthing; Canal / Strait = giriş ve çıkışlar).
 * Menüde olmayan türler (COSP, günlükler, bekleme, yük, kira) "other"dır. Konum ve "Key
 * Information" raporun alanlarından türetilir; saklanmaz. Saf işlevler: bozuk ya da eksik alan
 * atlanır, hata vermez (liste her raporu göstermeli).
 */

export const REPORT_TYPES = [
  'arrival',
  'port_daily',
  'departure',
  'anchoring',
  'anchor_aweigh',
  'drifting',
  'drift_departure',
  'canal_strait',
  'noon',
  'other',
] as const;
export type ReportType = (typeof REPORT_TYPES)[number];

/** Rapor türünün kayıt türleri (README ortak-2026-10-18 S2). */
export const REPORT_TYPE_KINDS: Readonly<Record<ReportType, readonly VoyageReportKind[]>> = {
  arrival: ['eosp', 'arrival'],
  port_daily: ['port_daily'],
  departure: ['departure'],
  anchoring: ['anchorage_start'],
  anchor_aweigh: ['anchorage_end'],
  drifting: ['drift_start'],
  drift_departure: ['drift_end'],
  canal_strait: ['strait_entry', 'strait_exit', 'canal_entry', 'canal_exit'],
  noon: ['noon'],
  other: [
    'cosp',
    'anchorage_daily',
    'drift_daily',
    'waiting_start',
    'waiting_end',
    'cargo_start',
    'cargo_complete',
    'on_hire',
    'off_hire',
  ],
};

export function reportTypeOf(kind: VoyageReportKind): ReportType {
  return REPORT_TYPES.find((type) => REPORT_TYPE_KINDS[type].includes(kind)) ?? 'other';
}

/**
 * "Reports by Type" kartının grupları (sefer-6; ADR 0082 Ek §4): Noon, liman günlükleri, varış ve
 * kalkış, demir ve sürüklenme, diğerleri (kanal/boğaz ve menüde olmayan türler).
 */
export const REPORT_GROUPS = [
  'noon',
  'port',
  'arrival_departure',
  'anchorage_drift',
  'other',
] as const;
export type ReportGroup = (typeof REPORT_GROUPS)[number];

export const REPORT_GROUP_OF: Readonly<Record<ReportType, ReportGroup>> = {
  arrival: 'arrival_departure',
  port_daily: 'port',
  departure: 'arrival_departure',
  anchoring: 'anchorage_drift',
  anchor_aweigh: 'anchorage_drift',
  drifting: 'anchorage_drift',
  drift_departure: 'anchorage_drift',
  canal_strait: 'other',
  noon: 'noon',
  other: 'other',
};

/**
 * Dönemdeki raporların grup sayıları (`since` dahil, UTC ISO). İptal edilen rapor sayılmaz;
 * taslak listede olduğu için sayılır. Her grup sıfır olsa da döner (kart lejantı sabit).
 */
export function reportsByGroup(
  reports: readonly { kind: VoyageReportKind; reportedAt: string; status: string }[],
  since: string,
): { group: ReportGroup; count: number }[] {
  const from = Date.parse(since);
  const counts = new Map<ReportGroup, number>(REPORT_GROUPS.map((g) => [g, 0]));
  for (const report of reports) {
    if (report.status === 'void' || Date.parse(report.reportedAt) < from) continue;
    const group = REPORT_GROUP_OF[reportTypeOf(report.kind)];
    counts.set(group, (counts.get(group) ?? 0) + 1);
  }
  return REPORT_GROUPS.map((group) => ({ group, count: counts.get(group) ?? 0 }));
}

/**
 * Listedeki durum: taslak, gönderildi, düzeltildi (yeniden açılıp tekrar gönderilmiş; ADR 0058
 * §5), yeniden açıldı, iptal (ADR 0077 §3).
 */
export const ALL_REPORT_STATUSES = ['draft', 'submitted', 'amended', 'reopened', 'void'] as const;
export type AllReportStatus = (typeof ALL_REPORT_STATUSES)[number];

export function allReportStatus(report: {
  status: 'draft' | 'submitted' | 'reopened' | 'void';
  revision: number;
}): AllReportStatus {
  if (report.status === 'submitted' && report.revision > 1) return 'amended';
  return report.status;
}

/** Listenin okuduğu rapor alanları (gemi ve ofis görünümü aynı). */
export interface ReportFields {
  kind: VoyageReportKind;
  reportedAt: string;
  lat: number | null;
  lon: number | null;
  schema: number;
  data: Record<string, unknown>;
}

export type ReportLocation =
  | { type: 'port'; name: string; code: string | null }
  | { type: 'area'; name: string }
  | { type: 'passage'; name: string }
  | { type: 'sea' | 'anchorage'; lat: number | null; lon: number | null }
  | { type: 'unknown' };

const record = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;
const number = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;
const instant = (value: unknown): string | null =>
  typeof value === 'string' && !Number.isNaN(Date.parse(value)) ? value : null;

const SEA_KINDS: ReadonlySet<VoyageReportKind> = new Set([
  'noon',
  'cosp',
  'eosp',
  'drift_start',
  'drift_end',
  'waiting_start',
  'waiting_end',
]);

/** Port / Location: limanda liman, günlükte konum durumu, boğaz ve kanalda adı, yoksa konum. */
export function reportLocation(report: ReportFields): ReportLocation {
  const data = record(report.data);
  const position = { lat: report.lat, lon: report.lon };
  const port = (name: unknown, code: unknown): ReportLocation | null => {
    const found = text(name);
    return found ? { type: 'port', name: found, code: text(code) } : null;
  };
  switch (report.kind) {
    case 'departure':
    case 'arrival':
      return port(data.port, data.portCode) ?? { type: 'unknown' };
    case 'port_daily':
    case 'anchorage_daily':
    case 'drift_daily': {
      const location = record(data.location);
      const area = text(location.area);
      return (
        port(location.port, location.portCode) ??
        (area ? { type: 'area', name: area } : null) ??
        (report.kind === 'anchorage_daily'
          ? { type: 'anchorage', ...position }
          : report.kind === 'drift_daily'
            ? { type: 'sea', ...position }
            : { type: 'unknown' })
      );
    }
    case 'strait_entry':
    case 'strait_exit':
    case 'canal_entry':
    case 'canal_exit': {
      const name = text(data.name);
      return name ? { type: 'passage', name } : { type: 'sea', ...position };
    }
    case 'anchorage_start':
    case 'anchorage_end':
      return { type: 'anchorage', ...position };
    default:
      return SEA_KINDS.has(report.kind) ? { type: 'sea', ...position } : { type: 'unknown' };
  }
}

/** Seçenek listesi: arayüz çevirisinin yeri buna göre seçilir. */
export type KeyInfoList =
  | 'loadCondition'
  | 'waitingReason'
  | 'nextOperation'
  | 'dailyActivity'
  | 'cargoOperation';

export type KeyInfoItem =
  | { key: string; type: 'number'; value: number; unit: 'kn' | 't' | 'nm'; digits: number }
  /** Yük miktarı ve birimi (`CARGO_UNITS`). */
  | { key: string; type: 'quantity'; value: number; unit: string }
  /** UTC ISO-8601. */
  | { key: string; type: 'time'; at: string }
  | { key: string; type: 'option'; list: KeyInfoList; value: string }
  | { key: string; type: 'text'; value: string }
  | { key: string; type: 'flag' };

/**
 * "Key Information" (sefer-6): raporun bir bakışta okunan değerleri, en çok üç. Noon hız, tüketim
 * ve mesafe; Departure pilot off; Arrival pilot on ve all fast; demir ve sürüklenme zamanları ve
 * gerekçe; boğaz ve kanal adı; günlükte faaliyet ve yük.
 */
export function reportKeyInfo(report: ReportFields): KeyInfoItem[] {
  const data = record(report.data);
  const items: (KeyInfoItem | null)[] = [];
  const num = (key: string, value: unknown, unit: 'kn' | 't' | 'nm', digits: number) => {
    const found = number(value);
    return found === null ? null : ({ key, type: 'number', value: found, unit, digits } as const);
  };
  const time = (key: string, value: unknown) => {
    const at = instant(value);
    return at ? ({ key, type: 'time', at } as const) : null;
  };
  const option = (key: string, list: KeyInfoList, value: unknown) => {
    const found = text(value);
    return found ? ({ key, type: 'option', list, value: found } as const) : null;
  };
  const label = (key: string, value: unknown) => {
    const found = text(value);
    return found ? ({ key, type: 'text', value: found } as const) : null;
  };
  const daily = () => {
    const location = record(data.location);
    const cargo = record(data.cargoProgress);
    const total = number(cargo.handledTotalQty);
    const unit = text(cargo.unit);
    return [
      option('activity', 'dailyActivity', location.activity),
      total !== null && unit
        ? ({ key: 'cargoTotal', type: 'quantity', value: total, unit } as const)
        : null,
    ];
  };

  switch (report.kind) {
    case 'noon': {
      const navigation = record(data.navigation);
      const fuels = record(data.fuel).fuels;
      const used = Array.isArray(fuels)
        ? fuels.map((row) => number(record(row).consumption)).filter((v) => v !== null)
        : [];
      const consumption =
        used.length > 0 ? Math.round(used.reduce((sum, v) => sum + v, 0) * 1000) / 1000 : null;
      items.push(
        num('speed', navigation.sog, 'kn', 1),
        num('consumption', consumption, 't', 1),
        num('distance', navigation.distanceSinceLast, 'nm', 0),
      );
      break;
    }
    case 'departure':
      // Sürüm 1'de rapor zamanı pilotun ayrılışıdır (ADR 0058 §1)
      items.push(
        report.schema === 1
          ? time('pilotOff', report.reportedAt)
          : data.pilotNotUsed === true
            ? { key: 'noPilot', type: 'flag' }
            : time('pilotOff', data.pilotOff),
        option('loadCondition', 'loadCondition', data.loadCondition),
        label('nextPort', data.nextPort),
      );
      break;
    case 'arrival':
      items.push(time('pilotOn', data.pilotOnBoard), time('allFast', data.allFast));
      break;
    case 'eosp':
      items.push(
        num('distanceToPort', data.distanceToPortNm, 'nm', 1),
        time('norTendered', record(data.nor).tenderedAt),
      );
      break;
    case 'cosp':
      items.push(
        num('speed', data.speedKn, 'kn', 1),
        option('loadCondition', 'loadCondition', data.loadCondition),
      );
      break;
    case 'port_daily':
    case 'anchorage_daily':
      items.push(...daily());
      break;
    case 'drift_daily':
      items.push(...daily(), num('distance', data.distanceSinceLast, 'nm', 1));
      break;
    case 'anchorage_start':
      items.push(
        time('anchored', report.reportedAt),
        option('reason', 'waitingReason', data.reason),
      );
      break;
    case 'anchorage_end':
      items.push(
        time('anchorAweigh', report.reportedAt),
        option('nextOperation', 'nextOperation', data.nextOperation),
      );
      break;
    case 'drift_start':
      items.push(
        time('driftStart', report.reportedAt),
        option('reason', 'waitingReason', data.reason),
      );
      break;
    case 'drift_end':
      items.push(
        time('driftEnd', report.reportedAt),
        num('distance', data.distanceSinceLast, 'nm', 1),
      );
      break;
    case 'waiting_start':
      items.push(
        time('waitingStart', report.reportedAt),
        option('reason', 'waitingReason', data.reason),
      );
      break;
    case 'waiting_end':
      items.push(time('waitingEnd', report.reportedAt));
      break;
    case 'strait_entry':
    case 'strait_exit':
    case 'canal_entry':
    case 'canal_exit':
      items.push(label('passage', data.name), num('distance', data.distanceSinceLast, 'nm', 1));
      break;
    case 'cargo_start':
    case 'cargo_complete':
      items.push(option('operation', 'cargoOperation', data.operation));
      break;
    case 'on_hire':
    case 'off_hire':
      items.push(label('charterParty', data.charterPartyNo));
      break;
  }
  return items.filter((item): item is KeyInfoItem => item !== null).slice(0, 3);
}
